import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useStructuredData } from '../hooks/useStructuredData';
import { useAuthContext } from '../contexts/AuthContext';
import { tournamentApi, leagueApi, Tournament, PublicLeague } from '../services/api-client';
import {
  TrophyIcon,
  BoltIcon,
  ChartBarIcon,
  UsersIcon,
  MapPinIcon,
  CalendarIcon,
  ScaleIcon,
} from '../components/icons';
import { formatDateRange } from '../utils/dateTime';

export default function LandingPage() {
  const { isAuthenticated } = useAuthContext();
  useDocumentMeta(
    'Home',
    'Takedown League is the free fantasy wrestling platform where fans draft NCAA wrestlers, compete in leagues with friends, and earn points from live tournament results.'
  );

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Takedown League',
    url: 'https://takedownleague.com',
    description:
      'The free fantasy wrestling platform where fans draft NCAA wrestlers, compete in leagues with friends, and earn points from live tournament results.',
    publisher: {
      '@type': 'Organization',
      name: 'Takedown League',
      url: 'https://takedownleague.com',
    },
  });

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [isUpcoming, setIsUpcoming] = useState(true);
  const [publicLeagues, setPublicLeagues] = useState<PublicLeague[]>([]);

  useEffect(() => {
    leagueApi
      .getPublicLeagues()
      .then(setPublicLeagues)
      .catch(() => {
        // Silently hide section on error
      });
  }, []);

  useEffect(() => {
    tournamentApi
      .list()
      .then((all) => {
        const now = new Date();
        const upcoming = all.filter((t) => t.startDate && new Date(t.startDate) > now);

        if (upcoming.length > 0) {
          upcoming.sort(
            (a, b) => new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime()
          );
          setTournaments(upcoming);
          setIsUpcoming(true);
        } else {
          const withDates = all.filter((t) => t.startDate);
          withDates.sort(
            (a, b) => new Date(b.startDate!).getTime() - new Date(a.startDate!).getTime()
          );
          setTournaments(withDates.slice(0, 4));
          setIsUpcoming(false);
        }
      })
      .catch(() => {
        // Silently hide section on error
      });
  }, []);

  return (
    <PublicLayout hideHeader={!isAuthenticated}>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-purple-700 text-white py-20 px-4">
        <div className="max-w-5xl mx-auto flex flex-col lg:flex-row items-center gap-12">
          <div className="text-center lg:text-left flex-1">
            <h1 className="text-5xl font-bold mb-4">Takedown League</h1>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl">
              The fantasy league for NCAA wrestling fans. Draft wrestlers, compete with friends, and
              prove you know the sport best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              {isAuthenticated ? (
                <Link
                  to="/leagues"
                  className="bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
                >
                  Go to My Leagues
                </Link>
              ) : (
                <>
                  <Link
                    to="/login?tab=register"
                    className="bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
                  >
                    Get Started
                  </Link>
                  <Link
                    to="/login"
                    className="border-2 border-white text-white font-semibold py-3 px-8 rounded-lg hover:bg-white/10 transition-colors text-lg"
                  >
                    Sign In
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="hidden lg:block flex-shrink-0">
            <img
              src="/og-image.png"
              alt="Takedown League preview"
              className="rounded-xl shadow-2xl w-[420px]"
            />
          </div>
        </div>
      </section>

      {/* Open Leagues Section */}
      <section className="bg-gradient-to-br from-purple-50 to-blue-50 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">
            Join a Public League
          </h2>
          {publicLeagues.length > 0 ? (
            <>
              <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
                No league to join yet? Pick a public league below and start drafting wrestlers in
                seconds — no invite code needed.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {publicLeagues.map((league) => (
                  <Link
                    key={league.id}
                    to={`/join/${league.inviteCode}`}
                    className="group bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex justify-center mb-4">
                      <TrophyIcon className="w-10 h-10 text-purple-600 group-hover:text-purple-700 transition-colors" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-700 transition-colors mb-2 text-center">
                      {league.name}
                    </h3>
                    {league.tournamentName && (
                      <p className="text-gray-600 text-sm text-center mb-1">
                        {league.tournamentName}
                      </p>
                    )}
                    <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <UsersIcon className="w-3.5 h-3.5" />
                        {league.memberCount} {league.memberCount === 1 ? 'team' : 'teams'}
                      </span>
                      {league.tournamentStartDate && (
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-3.5 h-3.5" />
                          {formatDateRange(league.tournamentStartDate, league.tournamentEndDate)}
                        </span>
                      )}
                    </div>
                    <div className="text-center">
                      <span className="inline-block bg-purple-600 text-white text-sm font-semibold py-2 px-5 rounded-lg group-hover:bg-purple-700 transition-colors">
                        Join League
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center">
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Public leagues drop before every major event — check back soon!
              </p>
              <Link
                to={isAuthenticated ? '/dashboard' : '/login'}
                className="inline-block bg-purple-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-purple-700 transition-colors"
              >
                {isAuthenticated ? 'Create Your Own League' : 'Sign Up to Create a League'}
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* See It In Action Section */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">See It In Action</h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            No signup required to see what you're getting. Here's how simple it is.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex flex-col items-center">
              <video
                src="/demo_blurred_2x.mp4"
                autoPlay
                muted
                loop
                playsInline
                className="rounded-xl shadow-lg w-full max-w-[300px]"
              />
              <p className="mt-4 text-gray-600 text-sm font-medium">Sign up and draft in seconds</p>
            </div>
            <div className="flex flex-col items-center">
              <img
                src="/scoring.jpeg"
                alt="Live scoring from a real tournament"
                className="rounded-xl shadow-lg w-full max-w-[300px]"
              />
              <p className="mt-4 text-gray-600 text-sm font-medium">
                Live scoring from real tournaments
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Why Takedown League?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-4">
                <TrophyIcon className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Leagues</h3>
              <p className="text-gray-600 text-sm">
                Start your own fantasy wrestling league for any NCAA tournament and invite friends
                to compete head-to-head.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-4">
                <BoltIcon className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Draft Wrestlers</h3>
              <p className="text-gray-600 text-sm">
                Use your budget to draft wrestlers based on their seed. Strategy matters — pick
                wisely.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-4">
                <ChartBarIcon className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Live Scoring</h3>
              <p className="text-gray-600 text-sm">
                Scores update based on real tournament results. Watch your team climb the
                leaderboard.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-4">
                <UsersIcon className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Invite Friends</h3>
              <p className="text-gray-600 text-sm">
                Share a simple invite link and get your crew into the league in seconds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">How It Works</h2>
          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Sign Up</h3>
                <p className="text-gray-600">
                  Create a free account with your email or Google. It only takes a few seconds.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">
                  Create or Join a League
                </h3>
                <p className="text-gray-600">
                  Start your own league and invite friends, or join an existing one with an invite
                  link.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Draft & Compete</h3>
                <p className="text-gray-600">
                  Draft wrestlers to your roster using your budget and earn points based on their
                  real NCAA tournament results. The best team wins.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tournaments Section */}
      {tournaments.length > 0 && (
        <section className="bg-gray-50 py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">
              {isUpcoming ? 'Upcoming Tournaments' : 'Recent Tournaments'}
            </h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              {isUpcoming
                ? 'Create a league for any of these upcoming NCAA wrestling tournaments and start competing with friends.'
                : "Here's what we've been up to. New tournaments will be posted as they become available."}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tournaments.map((t) => (
                <div key={t.id} className="bg-white rounded-lg shadow p-6 flex flex-col">
                  <Link
                    to={`/tournaments/${t.id}`}
                    className="text-lg font-semibold text-gray-900 mb-3 hover:text-purple-700 transition-colors"
                  >
                    {t.name}
                  </Link>
                  <div className="space-y-2 text-sm text-gray-600 mb-4 flex-1">
                    {t.location && (
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>{t.location}</span>
                      </div>
                    )}
                    {t.startDate && (
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>{formatDateRange(t.startDate, t.endDate)}</span>
                      </div>
                    )}
                    {t.weightClasses.length > 0 && (
                      <div className="flex items-center gap-2">
                        <ScaleIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>{t.weightClasses.length} weight classes</span>
                      </div>
                    )}
                  </div>
                  <Link
                    to={`/tournaments/${t.id}`}
                    className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                  >
                    View field & results →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ad Banner - Prominent placement after features */}
      <div className="bg-gray-100 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <AdBanner slot={AD_SLOTS.landing} format="rectangle" />
        </div>
      </div>

      {/* CTA Banner */}
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          {isAuthenticated ? (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Back to Your Leagues</h2>
              <p className="text-purple-100 mb-6">
                Head back to your leagues to manage your rosters and check standings.
              </p>
              <Link
                to="/leagues"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Back to My Leagues
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Compete?</h2>
              <p className="text-purple-100 mb-6">
                Join Takedown League today and start building your dream wrestling roster.
              </p>
              <Link
                to="/login?tab=register"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Create Your Free Account
              </Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
